import React from 'react';

/*----------------------------------
- COLOR PALETTE  (semantic, usable across CRM + UI)
----------------------------------*/

export type BadgeColor =
    | 'green' | 'amber' | 'red' | 'slate' | 'blue' | 'sky'
    | 'violet' | 'purple' | 'orange' | 'teal' | 'pink' | 'indigo';

export const BADGE_COLOR_CLASSES: Record<BadgeColor, string> = {
    green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber:  'bg-amber-50   text-amber-700   border-amber-200',
    red:    'bg-rose-50    text-rose-700    border-rose-200',
    slate:  'bg-slate-100  text-slate-600   border-slate-200',
    blue:   'bg-blue-50    text-blue-700    border-blue-200',
    sky:    'bg-sky-50     text-sky-700     border-sky-200',
    violet: 'bg-violet-50  text-violet-700  border-violet-200',
    purple: 'bg-purple-50  text-purple-700  border-purple-200',
    orange: 'bg-orange-50  text-orange-700  border-orange-200',
    teal:   'bg-teal-50    text-teal-700    border-teal-200',
    pink:   'bg-pink-50    text-pink-700    border-pink-200',
    indigo: 'bg-indigo-50  text-indigo-700  border-indigo-200',
};

/*----------------------------------
- PROPS
----------------------------------*/

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
    /** Semantic color palette — preferred over variant for CRM use */
    color?: BadgeColor;
    /** Dot indicator on the left */
    dot?: boolean;
    /** Legacy variant support */
    variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'outline' | 'applied' | 'approved' | 'neutral' | 'rejected' | 'amber' | 'purple';
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
}

/*----------------------------------
- LEGACY VARIANT MAP  (kept for backward compatibility)
----------------------------------*/

const VARIANT_CLASSES: Record<NonNullable<BadgeProps['variant']>, string> = {
    default:  'bg-gradient-to-r from-orange-500 to-pink-500 text-white border-transparent',
    success:  'bg-green-100 text-green-800 border-green-300 shadow-sm shadow-green-100',
    warning:  'bg-yellow-100 text-yellow-700 border-yellow-300',
    error:    'bg-rose-50 text-rose-700 border-rose-200',
    info:     'bg-blue-50 text-blue-700 border-blue-200',
    outline:  'bg-white border-slate-300 text-slate-700',
    applied:  'bg-orange-50 text-orange-700 border-orange-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    neutral:  'bg-slate-100 text-slate-600 border-slate-200',
    rejected: 'bg-rose-50 text-rose-700 border-rose-200',
    amber:    'bg-amber-50 text-amber-700 border-amber-200',
    purple:   'bg-purple-50 text-purple-700 border-purple-200',
};

const SIZE_CLASSES: Record<NonNullable<BadgeProps['size']>, string> = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-[13px]',
    lg: 'px-3 py-1 text-sm',
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
    ({ className, color, dot = false, variant = 'default', size = 'md', children, ...props }, ref) => {
        const colorCls = color
            ? BADGE_COLOR_CLASSES[color]
            : VARIANT_CLASSES[variant];

        const classes = [
            'inline-flex items-center gap-1.5 font-semibold rounded-full border transition-colors',
            SIZE_CLASSES[size],
            colorCls,
            className,
        ].filter(Boolean).join(' ');

        return (
            <span ref={ref} className={classes} {...props}>
                {dot && <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />}
                {children}
            </span>
        );
    },
);

Badge.displayName = 'Badge';

export default Badge;
